import { ChangeDetectionStrategy, Component, Renderer2 } from '@angular/core';
import { of, startWith, switchMap, take, tap } from 'rxjs';

import {
  Category,
  CategoryService,
  ConfigService,
  CustomerService,
  ProductionService,
  RouterService,
  StoreService,
  WishListConfigService,
  WishListApiService,
  SRouterLinkWithHrefDirective,
  TranslatePipe,
} from '@speedy/core';
import { StoreSwitcherComponent } from './store-switcher/store-switcher.component';
import { HeaderMainMenuComponent } from './header-main-menu/header-main-menu.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { TopCartComponent } from '../cart/top-cart/top-cart.component';
import { MatIcon } from '@angular/material/icon';
import { CustomerMenuComponent } from './customer-menu/customer-menu.component';
import { TopSearchComponent } from './top-search/top-search.component';
import { TopBarComponent } from './top-bar/top-bar.component';
import { TopBarMenuComponent } from './top-bar-menu/top-bar-menu.component';
import { CurrencyDropdownComponent } from './currency-dropdown/currency-dropdown.component';

const CATEGORIES_KEY = 'categories';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TopBarComponent,
    SRouterLinkWithHrefDirective,
    TopSearchComponent,
    CustomerMenuComponent,
    MatIcon,
    TopCartComponent,
    NgClass,
    HeaderMainMenuComponent,
    StoreSwitcherComponent,
    AsyncPipe,
    TranslatePipe,
    TopBarMenuComponent,
    CurrencyDropdownComponent,
  ],
})
export class HeaderComponent {
  categories$ = this.storeService.store$.pipe(
    startWith(this.storeService.getCurrentStore()),
    switchMap(() => {
      const categories =
        this.productionService.getDataFromTransferState<Category[]>(CATEGORIES_KEY);
      if (categories) {
        return of(categories as Category[]);
      } else {
        return this.categoryService.getCategories().pipe(
          take(1),
          tap((categories) => {
            this.productionService.setDataToTransferState(CATEGORIES_KEY, categories);
          }),
        );
      }
    }),
  );

  menuHidden = true;
  searchHidden = true;
  isShowWishlistNavigation = this.wishListService.isShowWishList();

  logo = {
    title: this.configService.getLogoAlt(),
    url: this.configService.getLogoImage(),
    width: this.configService.getLogoWidth(),
    height: this.configService.getLogoHeight(),
  };

  constructor(
    private configService: ConfigService,
    private renderer: Renderer2,
    private categoryService: CategoryService,
    private storeService: StoreService,
    private productionService: ProductionService,
    private customerService: CustomerService,
    private routerService: RouterService,
    private wishListService: WishListConfigService,
    private wishListApiService: WishListApiService,
  ) {}

  toggleMenu() {
    this.menuHidden = !this.menuHidden;
    this.searchHidden = true;
    const html = document.getElementsByTagName('html')[0];

    this.renderer.removeClass(html, 'sub-nav-open');
    this.renderer.removeClass(html, 'search-open');
  }

  toggleSearch() {
    if (this.productionService.isServerMode()) return;

    this.searchHidden = !this.searchHidden;
    this.menuHidden = true;
    const html = document.getElementsByTagName('html')[0];
    const topSearchInput = document.getElementById('topSearchInput');

    if (this.searchHidden) {
      this.renderer.removeClass(html, 'search-open');
    } else {
      setTimeout(() => {
        topSearchInput?.focus();
      }, 10);

      this.renderer.addClass(html, 'search-open');
    }
    this.renderer.removeClass(html, 'nav-open');
  }

  hideSearch() {
    if (this.productionService.isServerMode()) return;

    const html = document.getElementsByTagName('html')[0];
    this.searchHidden = true;
    this.renderer.removeClass(html, 'search-open');
  }

  redirectToWishlist() {
    if (this.customerService.isLoggedIn()) {
      this.routerService.navigate('/customer/wishlist');
    } else {
      this.wishListApiService.notifyUserToLogIn();
    }
  }
}
